// listTtlMs: a list call already returns every conflict in full, so single reads made right
// after it (login linking, loadConflicts) are answered from that list instead of one call each.
export function createConflictClient({ auth, baseUrl, fetchImpl = (...args) => fetch(...args), intervalMs = 5000, listTtlMs = 2000, now = () => Date.now() }) {
  let owner = null, serial = 0;
  const snapshots = new Map();
  const latest = new Map();
  const listed = new Map(); // id -> { record, at } from the last explicit list call
  const clone = value => JSON.parse(JSON.stringify(value));
  async function request(endpoint, payload) {
    const user = auth()?.currentUser;
    if (!user || user.isAnonymous) throw new Error('Verified sign-in required.');
    if (owner !== user) { snapshots.clear(); latest.clear(); listed.clear(); owner = user; }
    const token = await user.getIdToken();
    if (auth()?.currentUser !== user) throw new Error('Your sign-in has changed.');
    const response = await fetchImpl(`${baseUrl}/${endpoint}`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(payload), cache: 'no-store' });
    const result = await response.json();
    if (auth()?.currentUser !== user) throw new Error('Your sign-in has changed.');
    if (!response.ok) {
      const error = new Error(result.error || 'Conflict access failed.'); error.status = response.status; throw error;
    }
    return result;
  }
  function remember(record) {
    const existing = latest.get(record.id);
    if (existing && JSON.stringify(snapshots.get(existing)) === JSON.stringify(record)) {
      return { ...record, _accessRevision: existing };
    }
    const revision = String(++serial);
    snapshots.set(revision, clone(record)); latest.set(record.id, revision);
    while (snapshots.size > 250) snapshots.delete(snapshots.keys().next().value);
    return { ...record, _accessRevision: revision };
  }
  async function read(id) {
    const hit = listed.get(id);
    if (hit && owner === auth()?.currentUser && now() - hit.at < listTtlMs) return remember(clone(hit.record));
    try { return remember((await request('getAccessibleConflicts', { conflictId: id })).conflict); }
    catch (error) { if (error.status === 404) return null; throw error; }
  }
  async function fetchAll({ keep }) {
    const result = await request('getAccessibleConflicts', {});
    if (keep) {
      const at = now(); listed.clear();
      for (const entry of result.conflicts) listed.set(entry.conflict.id, { record: clone(entry.conflict), at });
    }
    return result.conflicts.map(entry => remember(entry.conflict));
  }
  const all = () => fetchAll({ keep: true });
  async function write(id, value) {
    const { _accessRevision, ...proposed } = value;
    listed.delete(id);
    if (owner !== auth()?.currentUser) { snapshots.clear(); latest.clear(); listed.clear(); }
    let base = _accessRevision ? snapshots.get(_accessRevision) : undefined;
    if (_accessRevision && !base) throw new Error('This view expired. Refresh before saving.');
    if (!base) {
      const current = await read(id);
      if (current) base = snapshots.get(current._accessRevision);
    }
    const result = await request('saveAuthorizedConflict', { conflictId: id, base: base || null, value: proposed });
    const saved = remember(result.conflict);
    // Existing callers retain the object after creation for invitation bookkeeping.
    Object.keys(value).forEach(key => delete value[key]); Object.assign(value, saved);
    return true;
  }
  function subscribe(callback) {
    let stopped = false, timer;
    const tick = async () => {
      try {
        // Background refreshes never feed the read cache, so edits always re-read live data.
        const records = await fetchAll({ keep: false });
        if (!stopped) callback({ exists: () => records.length > 0, val: () => Object.fromEntries(records.map(c => [c.id, c])) });
      } catch {
        if (!stopped) callback({ exists: () => false, val: () => null });
      } finally { if (!stopped) timer = setTimeout(tick, intervalMs); }
    };
    tick();
    return () => { stopped = true; clearTimeout(timer); };
  }
  async function remove(id) {
    listed.delete(id);
    await request('saveAuthorizedConflict', { action: 'delete', conflictId: id });
    latest.delete(id);
    return true;
  }
  return { read, all, write, subscribe, remove };
}
